import {
  Body,
  Container,
  Head,
  Heading,
  Html,
  Preview,
  Section,
  Text,
  Hr,
  Link,
} from "@react-email/components";
import * as React from "react";

interface ContactNotificationEmailProps {
  name: string;
  email: string;
  phone: string;
  company: string;
  employees: string;
  service?: string;
  message?: string;
}

export const ContactNotificationEmail = ({
  name,
  email,
  phone,
  company,
  employees,
  service,
  message,
}: ContactNotificationEmailProps) => {
  const serviceLabel = service ? getServiceLabel(service) : "Not specified";

  return (
    <Html>
      <Head />
      <Preview>
        New Lead: {name} from {company} ({employees} employees)
      </Preview>
      <Body style={main}>
        <Container style={container}>
          <Section style={logoSection}>
            <Heading style={logo}>New Contact Form Submission</Heading>
            <Text style={tagline}>Leija Insurance Group</Text>
          </Section>

          <Section style={box}>
            <Section style={highlightBox}>
              <Text style={highlightText}>
                <strong>New inquiry from {company}</strong>
              </Text>
              <Text style={highlightSubtext}>
                Remember to follow up within 24 hours
              </Text>
            </Section>

            <Hr style={divider} />

            <Section style={infoSection}>
              <Text style={sectionHeading}>Contact Information</Text>
              <Text style={infoItem}>
                <strong>Name:</strong> {name}
              </Text>
              <Text style={infoItem}>
                <strong>Email:</strong>{" "}
                <Link href={`mailto:${email}`} style={link}>
                  {email}
                </Link>
              </Text>
              <Text style={infoItem}>
                <strong>Phone:</strong>{" "}
                <Link href={`tel:${phone.replace(/[^0-9]/g, "")}`} style={link}>
                  {phone}
                </Link>
              </Text>
            </Section>

            <Section style={infoSection}>
              <Text style={sectionHeading}>Business Information</Text>
              <Text style={infoItem}>
                <strong>Company:</strong> {company}
              </Text>
              <Text style={infoItem}>
                <strong>Employees:</strong> {employees}
              </Text>
              <Text style={infoItem}>
                <strong>Service Interest:</strong> {serviceLabel}
              </Text>
            </Section>

            {message && (
              <Section style={infoSection}>
                <Text style={sectionHeading}>Additional Information</Text>
                <Text style={messageText}>{message}</Text>
              </Section>
            )}
          </Section>

          <Section style={footer}>
            <Text style={footerText}>
              Received:{" "}
              {new Date().toLocaleString("en-US", {
                dateStyle: "medium",
                timeStyle: "short",
              })}
            </Text>
            <Text style={footerCopyright}>leijainsurancegroup.com</Text>
          </Section>
        </Container>
      </Body>
    </Html>
  );
};

const getServiceLabel = (service: string): string => {
  const serviceLabels: Record<string, string> = {
    health: "Group Health Insurance",
    retirement: "Retirement Planning",
    life: "Life Insurance",
    supplemental: "Supplemental Coverage",
    multiple: "Multiple Services",
  };
  return serviceLabels[service] || service;
};

ContactNotificationEmail.PreviewProps = {
  name: "John Smith",
  email: "john@acmecorp.com",
  phone: "512-555-0123",
  company: "Acme Corp",
  employees: "11-20",
  service: "health",
  message:
    "Looking to improve our current health insurance plan and potentially add retirement benefits for our team.",
} as ContactNotificationEmailProps;

export default ContactNotificationEmail;

const main = {
  backgroundColor: "#f6f9fc",
  fontFamily:
    '-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,"Helvetica Neue",Ubuntu,sans-serif',
  padding: "40px 0",
};

const container = {
  backgroundColor: "#ffffff",
  margin: "0 auto",
  borderRadius: "8px",
  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
  maxWidth: "600px",
};

const logoSection = {
  backgroundColor: "#1e293b",
  padding: "32px 48px",
  textAlign: "center" as const,
  borderRadius: "8px 8px 0 0",
};

const logo = {
  fontSize: "24px",
  fontWeight: "bold",
  color: "#ffffff",
  margin: "0 0 4px 0",
};

const tagline = {
  fontSize: "14px",
  color: "#cbd5e1",
  margin: "0",
};

const box = {
  padding: "48px",
};

const highlightBox = {
  backgroundColor: "#f1f5f9",
  borderLeft: "4px solid #475569",
  padding: "16px 20px",
  margin: "0 0 24px 0",
  borderRadius: "4px",
};

const highlightText = {
  color: "#334155",
  fontSize: "16px",
  margin: "0 0 8px 0",
};

const highlightSubtext = {
  color: "#64748b",
  fontSize: "14px",
  margin: "0",
};

const divider = {
  borderColor: "#e2e8f0",
  margin: "32px 0",
};

const infoSection = {
  marginBottom: "32px",
};

const sectionHeading = {
  fontSize: "18px",
  fontWeight: "600",
  color: "#1e293b",
  margin: "0 0 16px 0",
};

const infoItem = {
  fontSize: "15px",
  color: "#475569",
  lineHeight: "26px",
  margin: "0 0 12px 0",
};

const messageText = {
  fontSize: "15px",
  color: "#475569",
  lineHeight: "26px",
  margin: "0",
  padding: "16px",
  backgroundColor: "#f8fafc",
  borderRadius: "6px",
  whiteSpace: "pre-wrap" as const,
};

const link = {
  color: "#475569",
  textDecoration: "underline",
  fontWeight: "500",
};

const footer = {
  backgroundColor: "#f8fafc",
  padding: "24px 48px",
  textAlign: "center" as const,
  borderRadius: "0 0 8px 8px",
};

const footerText = {
  fontSize: "13px",
  color: "#64748b",
  margin: "0 0 8px 0",
};

const footerCopyright = {
  fontSize: "12px",
  color: "#94a3b8",
  margin: "0",
};
